<svelte:window on:keydown={keyDown} />

<div class="prom-combo__list-box" bind:this={listBoxContainer}
    role="listbox"
    id={listboxID}
    aria-labelledby={labelID}
    tabindex="-1">

    {#each options as option, i}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-interactive-supports-focus -->
        <div class="prom-combo__option-container" 
            role="option"
            id="option_{i}"
            aria-selected={option.value === selected}
            on:click={() => select(option)}>
            <div class="prom-combo__option-content">{option.label}</div>
        </div>
    {/each}
</div>

<script>
    import { createEventDispatcher, onMount } from 'svelte';

    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { isPrintableCharacter } from './helpers';

    export let id;
    export let options;
    export let selected;

    let listBoxContainer;
    let focusedIndex;

    $: labelID = 'label_' + id;
    $: listboxID = 'listbox_' + id;

    const dispatch = createEventDispatcher();

    onMount(() => {
        setSelectedElementToBeFocused();
    });

    function select(selectedOption) {
        dispatch('select', selectedOption);
    }

    export function checkIfFocused() {
        return document.activeElement === listBoxContainer;
    }

    function keyDown(event) {
        const key = normalizeKey(event.key);

        if (isPrintableCharacter(key)) {
            removeCurrentFocus();
            dispatch('returnFocusAfterKeyPressed', key);
            return;
        }

        if (event.shiftKey) {
            return;
        }

        switch (key) {
            case Keys.DOWN: {
                setNextElementToBeFocused('down', 'single');
                event.preventDefault();
                break;
            }
            case Keys.UP: {
                setNextElementToBeFocused('up', 'single');
                event.preventDefault();
                break;
            }
            case 'PageDown':
                setNextElementToBeFocused('down', 'page');
                event.preventDefault();
                break;
            case 'PageUp':
                setNextElementToBeFocused('up', 'page');
                event.preventDefault();
                break;
            case 'Home':
                setNextElementToBeFocused('up', 'all');
                event.preventDefault();
                break;
            case 'End':
                setNextElementToBeFocused('down', 'all');
                event.preventDefault();
                break;
            case Keys.ENTER: {
                const items = Array.from(listBoxContainer.querySelectorAll('.prom-combo__option-container'));
                const focusedItem = listBoxContainer.querySelector('.focused');
                const focusedIndex = items.findIndex(e => e === focusedItem);
                if (focusedIndex >= 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    dispatch('select', options[focusedIndex]);
                }
                break;
            }
        }
    }

    function setElementToBeFocused(index) {
        if (index >= 0) {
            removeCurrentFocus();
            const optionID = `option_${index}`;
            const focusedItem = listBoxContainer.querySelector('#' + optionID);
            changeFocused(focusedItem, index);
            scroll(focusedItem);
        }
    }

    function removeCurrentFocus() {
        const current = listBoxContainer.querySelector('.focused');
        if (current) {
            current.classList.remove('focused');
        }
    }

    function setSelectedElementToBeFocused() {
        focusedIndex = selected 
            ? options.findIndex(option => option.value === selected)
            : 0;
        
        setElementToBeFocused(focusedIndex);
    }

    /** 
     * Set next element in popup to be focused
     * @param {'single'|'page'|'all'} change single, page or an entire list depending on Arrow keys, page up/down or home/end keys pressed
     * @param {'down'|'up'} direction of focus change
    */
    function setNextElementToBeFocused(direction, change) {
        if (listBoxContainer) {
            /** @type {Array.<HTMLElement>} */
            const items = Array.from(listBoxContainer.querySelectorAll('.prom-combo__option-container'));
            const focusedItem = listBoxContainer.querySelector('.focused');
            if (items.length > 0) {
                const focusedIndex = focusedItem 
                    ? items.findIndex(e => e === focusedItem)
                    : 0;
    
                const nextFocusedIndex = focusedItem 
                    ? getNextFocusedIndex(items.length, focusedIndex, direction, change)
                    : 0;
                const nextFocused = items[nextFocusedIndex];
                changeFocused(nextFocused, nextFocusedIndex);
    
                scroll(nextFocused);

                handleNextFocusedOption(options[nextFocusedIndex]);
            }
        }
    }

    function handleNextFocusedOption(option) {
        dispatch('focusedOptionChanged', option);
    }

    /**
     * Get positions of option items
     * @returns {Array.<number>}
     */
     function getItemsPositions() {
        const items = listBoxContainer.querySelectorAll('.prom-combo__option-container');
        const positions = [];
        for (let i = 0; i < items.length; i++) {
            const item = /** @type {HTMLElement} */ (items[i]);
            positions.push(item.offsetTop);
        }
        return positions;
    }

    /** 
     * Get an index of next focused option
     * @param {number} length suggested itsms length
     * @param {number} index index of current focused element
     * @param {'down'|'up'} direction direction of focus change
     * @param {'single'|'page'|'all'} change single, page or all depending on Arrow keys, page up/down or home/end keys pressed
    */
    function getNextFocusedIndex(length, index, direction, change) {
        switch (change) {
            case 'all':
                return direction === 'down' ? length - 1 : 0;
            case 'page':
                return getNextFocusedIndexForPage(length, index, direction);
            case 'single':
                return getNextFocusedForSingleChange(length, index, direction);
        }
    }

    /**
     * Get an index of next focused option in case of single change
     * @param {number} length suggested itsms length
     * @param {number} index index of current focused element
     * @param {'down'|'up'} direction direction of focus change
     */
    function getNextFocusedForSingleChange(length, index, direction) {
        switch (direction) {
            case 'down':
                return index === length - 1
                    ? 0
                    : index + 1 >= length ? length - 1 : index + 1;
            case 'up':
                return index === 0
                    ? length - 1
                    : index - 1 < 0 ? 0 : index - 1;
        }
    }

    /** 
     * Get an index of next focused option if page changed
     * @param {number} length suggested itsms length
     * @param {number} index index of current focused element
     * @param {'down'|'up'} direction direction of focus change
    */
    function getNextFocusedIndexForPage(length, index, direction) {
        const popupHeight = listBoxContainer.offsetHeight;
        const positions = getItemsPositions();
        const currentPosition = positions[index];
        /** @type {number} */
        let nextPosition;
        /** @type {number} */
        let nextPositionIndex;
        /** @type {Array.<number>} */
        let positionsBefore;
        switch (direction) {
            case 'down':
                nextPosition = currentPosition + popupHeight;
                positionsBefore = positions.filter(p => p <= nextPosition);
                nextPositionIndex = positionsBefore.length - 1;
                return nextPositionIndex === index
                    ? 0
                    : nextPositionIndex;
            case 'up':
                nextPosition = currentPosition - popupHeight;
                positionsBefore = positions.filter(p => p <= nextPosition);
                nextPositionIndex = positionsBefore.length - 1 < 0
                    ? 0
                    : positionsBefore.length - 1;
                return nextPositionIndex === index
                    ? length - 1
                    : nextPositionIndex;
        }
    }

    /**
     * Scroll to the focused item
     * @param {HTMLElement} focusedItem element which should become visible after scrolling
     */
    function scroll(focusedItem) {
        const top = focusedItem.offsetTop;
        const bottom = top + focusedItem.offsetHeight + 1;
        if (bottom > listBoxContainer.scrollTop + listBoxContainer.offsetHeight) {
            listBoxContainer.scrollTop =  bottom - listBoxContainer.offsetHeight + 1;
        }
        if (top < listBoxContainer.scrollTop) {
            listBoxContainer.scrollTop = top;
        }
    }

    /**
     * Set focused element in the popup
     * @param {HTMLElement} element which should be focused
     * @param {number} index of that element
     */
    function changeFocused(element, index = 0) {
        const previous = listBoxContainer.querySelector('.focused');
        if (previous) {
            previous.classList.remove('focused');
            previous.removeAttribute('aria-selected');
        }

        element.classList.add('focused');
        element.setAttribute('aria-selected', 'true');
        focusedIndex = index;
    }
</script>